/**
 * Passkey / WebAuthn client (mig 0040, 2026-05-09).
 *
 * Wraps `@simplewebauthn/browser` to give the rest of the app a small,
 * stable surface:
 *   • `isPasskeySupported()` — feature detection (browser + context)
 *   • `registerPasskey(deviceName?)` — set up Face ID / fingerprint
 *   • `authenticateWithPasskey()` — sign in via biometric prompt
 *   • `listPasskeys() / removePasskey(id)` — settings management
 *   • `hasLocalPasskeyFlag()` / `setLocalPasskeyFlag()` /
 *     `clearLocalPasskeyFlag()` — localStorage marker the auth modal
 *     consults to decide whether to offer the "Welcome back, Face ID"
 *     fast path on app boot.
 *
 * Why a localStorage flag (instead of probing /api/auth/.../credentials):
 * the credentials endpoint requires auth. On a cold app boot the user
 * has no session — we wouldn't know if they have a passkey. The flag is
 * set after successful registration AND on each successful passkey
 * authentication, cleared when the last passkey is removed via settings.
 *
 * NOT shown in Telegram Mini App: passkey API is blocked there. We
 * detect via `window.Telegram?.WebApp?.initData` and skip all flows.
 */
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';

const PASSKEY_FLAG_KEY = 'rz_has_passkey';
const PASSKEY_DISMISSED_KEY = 'rz_passkey_setup_dismissed';

export interface Passkey {
  id: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface PasskeyAuthUser {
  id: string;
  displayName: string;
  email: string | null;
}

export type PasskeyOpResult =
  | { ok: true }
  | { ok: false; reason: 'user_cancelled' | 'not_supported' | 'options_failed' | 'verify_failed' | 'unknown' };

export type PasskeyAuthResult =
  | { ok: true; user: PasskeyAuthUser }
  | { ok: false; reason: 'user_cancelled' | 'not_supported' | 'options_failed' | 'verify_failed' | 'no_credentials' | 'unknown' };

// ─────────────────────────────────────────────────────────────────────────
// Feature detection
// ─────────────────────────────────────────────────────────────────────────

function isInTelegramWebApp(): boolean {
  // Same check `frontend/src/main.ts` uses — `Telegram.WebApp.initData`
  // is only populated when the page runs inside the @roamzy_bot Mini App.
  const tg = (window as unknown as { Telegram?: { WebApp?: { initData?: string } } }).Telegram;
  return Boolean(tg?.WebApp?.initData && tg.WebApp.initData.length > 0);
}

export function isPasskeySupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (isInTelegramWebApp()) return false;
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// localStorage flag helpers
// ─────────────────────────────────────────────────────────────────────────

export function hasLocalPasskeyFlag(): boolean {
  try {
    return localStorage.getItem(PASSKEY_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function setLocalPasskeyFlag(): void {
  try { localStorage.setItem(PASSKEY_FLAG_KEY, '1'); } catch { /* private mode */ }
}

export function clearLocalPasskeyFlag(): void {
  try { localStorage.removeItem(PASSKEY_FLAG_KEY); } catch { /* */ }
}

/**
 * Reconcile the local `rz_has_passkey` flag with server truth.
 *
 * Why: the flag can desynchronize:
 *   • User registers a passkey on iPhone — flag set on iPhone localStorage
 *     only. Mac doesn't know, even though iCloud Keychain syncs the
 *     credential to Mac automatically.
 *   • User clears Safari local data without logging out (Mac/iOS
 *     "Manage website data" → Roamzy → Remove).
 *   • Admin impersonate-as-user flow operates on different user_id
 *     contexts within same browser localStorage.
 *
 * Solution: ask the server who has passkey credentials for the current
 * user, and update local flag to match. Cheap (single GET) and safe
 * to call any time the user is authenticated. Don't call before
 * sign-in — endpoint is auth-gated, will 401.
 *
 * Operator caught flag-desync 2026-05-09 поздний вечер +15: «просит
 * sign in faster при каждом логине, хотя я уже регистрировал passkey».
 */
export async function reconcilePasskeyFlag(): Promise<void> {
  if (!isPasskeySupported()) return;
  try {
    const list = await listPasskeys();
    if (list.length > 0) {
      setLocalPasskeyFlag();
    } else {
      clearLocalPasskeyFlag();
    }
  } catch {
    // Network/auth error — leave flag as-is. Will reconcile on next call.
  }
}

export function isSetupDismissed(): boolean {
  try { return localStorage.getItem(PASSKEY_DISMISSED_KEY) === '1'; } catch { return false; }
}

export function markSetupDismissed(): void {
  try { localStorage.setItem(PASSKEY_DISMISSED_KEY, '1'); } catch { /* */ }
}

// ─────────────────────────────────────────────────────────────────────────
// Registration (requires existing session)
// ─────────────────────────────────────────────────────────────────────────

export async function registerPasskey(deviceName?: string): Promise<PasskeyOpResult> {
  if (!isPasskeySupported()) return { ok: false, reason: 'not_supported' };

  let optionsRes: Response;
  try {
    optionsRes = await fetch('/api/auth/webauthn/register/options', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
  } catch {
    return { ok: false, reason: 'options_failed' };
  }
  if (!optionsRes.ok) return { ok: false, reason: 'options_failed' };
  // Defensive parse — a misbehaving proxy or 200-with-HTML-body can throw
  // SyntaxError here. Catch so we return a clean error instead of an
  // unhandled promise rejection.
  let options;
  try { options = await optionsRes.json(); }
  catch { return { ok: false, reason: 'options_failed' }; }

  let attestation;
  try {
    attestation = await startRegistration({ optionsJSON: options });
  } catch (err) {
    const name = (err as Error)?.name;
    if (name === 'NotAllowedError' || name === 'AbortError') {
      return { ok: false, reason: 'user_cancelled' };
    }
    return { ok: false, reason: 'unknown' };
  }

  let verifyRes: Response;
  try {
    verifyRes = await fetch('/api/auth/webauthn/register/verify', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        response: attestation,
        ...(deviceName ? { deviceName } : {}),
      }),
    });
  } catch {
    return { ok: false, reason: 'verify_failed' };
  }
  if (!verifyRes.ok) return { ok: false, reason: 'verify_failed' };

  setLocalPasskeyFlag();
  return { ok: true };
}

// ─────────────────────────────────────────────────────────────────────────
// Authentication (no existing session needed)
// ─────────────────────────────────────────────────────────────────────────

export async function authenticateWithPasskey(): Promise<PasskeyAuthResult> {
  if (!isPasskeySupported()) return { ok: false, reason: 'not_supported' };

  let optionsRes: Response;
  try {
    optionsRes = await fetch('/api/auth/webauthn/authenticate/options', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    return { ok: false, reason: 'options_failed' };
  }
  if (!optionsRes.ok) return { ok: false, reason: 'options_failed' };
  // Defensive JSON parse — see registration helper for rationale.
  let options;
  try { options = await optionsRes.json(); }
  catch { return { ok: false, reason: 'options_failed' }; }

  let assertion;
  try {
    assertion = await startAuthentication({ optionsJSON: options });
  } catch (err) {
    const name = (err as Error)?.name;
    if (name === 'NotAllowedError' || name === 'AbortError') {
      return { ok: false, reason: 'user_cancelled' };
    }
    // Some browsers throw with no name when there's no discoverable credential
    // for this site (e.g. user removed their passkey from another device, or
    // never had one). Treat as no_credentials so caller can fall back to OAuth.
    return { ok: false, reason: 'no_credentials' };
  }

  let verifyRes: Response;
  try {
    verifyRes = await fetch('/api/auth/webauthn/authenticate/verify', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ response: assertion }),
    });
  } catch {
    return { ok: false, reason: 'verify_failed' };
  }
  if (!verifyRes.ok) {
    // 404 = credential not in DB (was deleted server-side). Clear local flag
    // so the welcome-back UI doesn't promise something we can't deliver next time.
    if (verifyRes.status === 404) clearLocalPasskeyFlag();
    return { ok: false, reason: 'verify_failed' };
  }

  // Defensive JSON parse — return verify_failed rather than rejecting.
  let data: { user?: PasskeyAuthUser } | null = null;
  try { data = await verifyRes.json(); }
  catch { return { ok: false, reason: 'verify_failed' }; }
  if (!data?.user) return { ok: false, reason: 'verify_failed' };

  // Refresh the flag — confirms passkey is still working server-side.
  setLocalPasskeyFlag();
  return { ok: true, user: data.user };
}

// ─────────────────────────────────────────────────────────────────────────
// Management (settings page)
// ─────────────────────────────────────────────────────────────────────────

export async function listPasskeys(): Promise<Passkey[]> {
  try {
    const res = await fetch('/api/auth/webauthn/credentials', { credentials: 'include' });
    if (!res.ok) return [];
    let data: unknown;
    try { data = await res.json(); }
    catch { return []; }
    const creds = (data as { credentials?: unknown })?.credentials;
    return Array.isArray(creds) ? (creds as Passkey[]) : [];
  } catch {
    return [];
  }
}

export async function removePasskey(id: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/auth/webauthn/credentials/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) return false;
  } catch {
    return false;
  }
  // If this was the last passkey, clear the local flag so the welcome-back
  // UI doesn't bug the user about Face ID anymore.
  const remaining = await listPasskeys();
  if (remaining.length === 0) clearLocalPasskeyFlag();
  return true;
}
